import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";

import { functions } from "../../../../firebase";
import { buildBrochureEmailHtml } from "../../../../pages/clientHom/brochureEmailTemplate";
import "./TradeBrochureModal.css";

const BROCHURE_STORAGE_PATH =
  "TPC/TechPerformanceCentre/brochures/Programme-Brochure.pdf";
const BROCHURE_FILENAME = "Price-Action-Trader-Bootcamp-Brochure.pdf";

export default function TradeBrochureModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && status !== "sending") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open, status]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    setMessage("");

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const trimmedName = name.trim();

      const sendEmail = httpsCallable(functions, "sendEmail");
      await sendEmail({
        to: normalizedEmail,
        from: "AndUs <noreply@mail.andus.co.za>",
        subject: "Your Price Action Trader Bootcamp brochure",
        isHTML: true,
        html: buildBrochureEmailHtml({
          studentName: trimmedName,
          brochureTitle: "Price Action Trader Bootcamp",
        }),
        attachmentStoragePath: BROCHURE_STORAGE_PATH,
        attachmentFilename: BROCHURE_FILENAME,
      });

      try {
        const firestoreOperations = httpsCallable(
          functions,
          "firestoreOperations",
        );
        await firestoreOperations({
          operation: "write",
          company: "TPC_Group",
          collectionBrand: "TechPerformanceCentre",
          systemName: "tradeBrochureRequests",
          uniqueId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          documentData: {
            Program: "Price Action Trader Bootcamp",
            FullName: trimmedName,
            Email: normalizedEmail,
            Timestamp: new Date().toISOString(),
            Source: "Trade register page",
          },
        });
      } catch (loggingError) {
        console.warn(
          "[TradeBrochure] Email sent, but lead logging failed:",
          loggingError,
        );
      }

      setStatus("success");
      setMessage(`We sent the brochure to ${normalizedEmail}.`);
    } catch (error) {
      console.error("[TradeBrochure] Could not send brochure:", error);
      setStatus("error");
      setMessage(
        "We couldn't send the brochure right now. Please try again.",
      );
    }
  };

  return (
    <div
      className="trade-brochure-modal__backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && status !== "sending") {
          onClose();
        }
      }}
    >
      <section
        className="trade-brochure-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trade-brochure-modal-title"
      >
        <button
          type="button"
          className="trade-brochure-modal__close"
          onClick={onClose}
          disabled={status === "sending"}
          aria-label="Close brochure request"
        >
          ×
        </button>

        <p className="trade-brochure-modal__eyebrow">Trade Performance Centre</p>
        <h2 id="trade-brochure-modal-title">Get the Price Action Trader Bootcamp brochure</h2>
        <p className="trade-brochure-modal__intro">
          Tell us your name and email and we&rsquo;ll send the brochure directly to your inbox.
        </p>

        {status === "success" ? (
          <div className="trade-brochure-modal__success" role="status">
            <span aria-hidden="true">✓</span>
            <strong>Check your inbox</strong>
            <p>{message}</p>
            <button type="button" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label htmlFor="trade-brochure-name">Full name</label>
            <input
              ref={inputRef}
              id="trade-brochure-name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Your full name"
              autoComplete="name"
              disabled={status === "sending"}
              required
            />

            <label htmlFor="trade-brochure-email">Email</label>
            <input
              id="trade-brochure-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={status === "sending"}
              required
            />

            <button
              className="trade-brochure-modal__submit"
              type="submit"
              disabled={status === "sending"}
            >
              {status === "sending" ? "Sending brochure…" : "Email me the brochure"}
            </button>
            {status === "error" && (
              <p className="trade-brochure-modal__error" role="alert">{message}</p>
            )}
            <p className="trade-brochure-modal__privacy">
              We&rsquo;ll only use your details to send the brochure and relevant programme information.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
